/* eslint-disable react-refresh/only-export-components */
import { Navigate, Outlet, Route, useOutletContext } from "react-router-dom";

import { PermissionRoute } from "@/components/routing/PermissionRoute";
import type { RouteConfig, RouteConfigurations } from "@/types/route-types";
import { RouteWrapper } from "@/utils/RouteWrapper";

export const PassThroughOutlet = () => {
  const context = useOutletContext();
  return <Outlet context={context} />;
};

export const renderRoutes = (
  routes: RouteConfig[],
  parentConfigs?: {
    wrapper?: React.ComponentType<{ children: React.ReactNode }>;
    routeConfigurations?: RouteConfigurations;
  }
): React.ReactElement[] => {
  return routes.map((route, index) => {
    const key = route.path || `route-${index}`;

    if (route.redirect) {
      return (
        <Route
          key={key}
          {...(route.index ? { index: true } : { path: route.path })}
          element={<Navigate to={route.redirect} replace relative="path" />}
        />
      );
    }

    const RouteElement = route.element;
    const isLeafRoute = RouteElement && RouteElement !== Outlet;
    const effectiveWrapper = route.wrapper || parentConfigs?.wrapper;
    const effectiveConfigurations = route.routeConfigurations
      ? { ...parentConfigs?.routeConfigurations, ...route.routeConfigurations }
      : parentConfigs?.routeConfigurations;

    let routeElement;

    if (isLeafRoute) {
      const element =
        typeof RouteElement === "function" ? <RouteElement /> : RouteElement;

      routeElement = (
        <RouteWrapper
          Wrapper={effectiveWrapper}
          routeConfigurations={effectiveConfigurations}
        >
          {route.permission ? (
            <PermissionRoute
              entity={route.permission.entity}
              action={route.permission.action}
              targetRole={route.permission.targetRole}
              fallbackPath={route.permission.fallbackPath}
            >
              {element}
            </PermissionRoute>
          ) : (
            element
          )}
        </RouteWrapper>
      );
    } else {
      routeElement = <PassThroughOutlet />;
    }

    if (route.index) {
      return <Route key={key} index element={routeElement} />;
    }

    return (
      <Route key={key} path={route.path} element={routeElement}>
        {route.children &&
          renderRoutes(route.children, {
            wrapper: effectiveWrapper,
            routeConfigurations: effectiveConfigurations,
          })}
      </Route>
    );
  });
};
